import { useMutation, useQueryClient } from '@tanstack/react-query';
import { can } from '@pl/domain';
import type { MeDto, Role } from '@pl/domain';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useMatches, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { dayLabel } from '../lib/format';
import { useDiscrepancies, useMe, useOrders, useSites, useTransfers } from '../lib/queries';
import { LiveProvider } from '../lib/useLive';
import { Icon } from '../ui/Icon';
import { LiveDot } from '../ui/LiveDot';
import s from './OfficeLayout.module.css';
import { useMenuDrawer } from './useMenuDrawer';
import { useSiteScope } from './useSiteScope';

interface RouteHandle {
  title?: string;
  /** New-order-style pages own their own site picker; hide the shared top-bar one and the "New order" shortcut. */
  minimalHeader?: boolean;
  /** Company-wide pages (Settings): no site scope to pick and no "New order" shortcut. */
  companyWide?: boolean;
  /** Replaces the "{company} · {date}" line above the title, e.g. "Settings › Import". */
  crumb?: string;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

function roleLabel(role: Role, siteIds: string[] | null, siteCodes: Map<string, string>): string {
  const cap = role.charAt(0).toUpperCase() + role.slice(1);
  const scope = siteIds && siteIds.length > 0 ? siteIds.map((id) => siteCodes.get(id) ?? id).join(', ') : 'all sites';
  return `${cap} · ${scope}`;
}

/**
 * What the office streams. Someone with every site streams `all`, whatever the top bar is filtered
 * to: the dashboard's site cards and its stock list cover every site regardless. Someone restricted
 * to sites cannot open `all` (the server refuses it), so they stream the filtered site when it is
 * one of theirs and their first site otherwise.
 */
function streamSite(me: MeDto | undefined, scoped: string | undefined): string | null {
  if (!me) return null;
  if (me.siteIds === null) return 'all';
  if (scoped && me.siteIds.includes(scoped)) return scoped;
  return me.siteIds[0] ?? null;
}

export function OfficeLayout() {
  const me = useMe();
  const { siteId } = useSiteScope();
  return (
    <LiveProvider site={streamSite(me.data, siteId)}>
      <OfficeShell />
    </LiveProvider>
  );
}

function OfficeShell() {
  const me = useMe();
  const sites = useSites().data ?? [];
  const orders = useOrders({ status: ['open', 'picking'], limit: 200 });
  const onTheRoad = useTransfers({ status: ['in_transit'], limit: 200 });
  const unresolved = useDiscrepancies({ status: 'open', limit: 200 });
  const { siteId, setSiteId } = useSiteScope();
  const matches = useMatches();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const menu = useMenuDrawer();

  const signOut = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      qc.clear();
      nav('/login', { replace: true });
    },
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== '/') return;
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || active?.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const siteCodes = useMemo(() => new Map(sites.map((s) => [s.id, s.shortCode])), [sites]);
  const routeHandle = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const handle = matches[i]!.handle as RouteHandle | undefined;
      if (handle) return handle;
    }
    return {} as RouteHandle;
  }, [matches]);
  const title = routeHandle.title ?? '';
  const minimalHeader = Boolean(routeHandle.minimalHeader);
  const companyWide = Boolean(routeHandle.companyWide);

  if (!me.data) return null;
  const liveCount = sites.filter((s) => s.live).length;
  const firstTz = sites[0]?.timezone ?? 'UTC';

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const code = query.trim();
    if (!code) return;
    nav(`/office/boxes/${encodeURIComponent(code)}`);
    setQuery('');
  };

  return (
    <div className="office">
      <div className="shell">
        {/* Below 900 px, a drawer: a dialog while open (useMenuDrawer, office.css). */}
        <aside
          className="side"
          id="office-menu"
          ref={menu.drawerRef}
          data-open={menu.open || undefined}
          {...(menu.open ? { role: 'dialog', 'aria-modal': true, 'aria-label': 'Menu' } : {})}
        >
          <div className="brand">
            <div className="brand-mark">
              <i />
            </div>
            <div className="brand-name">
              PALLET<span>·</span>LEVEL
            </div>
            <button type="button" className="ico-btn side-close" aria-label="Close menu" onClick={menu.close}>
              <Icon name="x" />
            </button>
          </div>
          <div className="org">
            <div>
              <b>{me.data.org.name}</b>
              <small>
                {sites.length} sites · {liveCount} live
              </small>
            </div>
            <span style={{ color: '#7d838c' }}>
              <Icon name="chev-down" size={14} />
            </span>
          </div>
          <nav className="nav" aria-label="Office">
            <NavLink to="/office" end className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="dashboard" />
              Dashboard
            </NavLink>
            <NavLink to="/office/orders" className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="orders" />
              Orders
              {orders.data && <span className="count">{orders.data.length}</span>}
            </NavLink>
            <NavLink to="/office/stock" className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="stock" />
              Stock
            </NavLink>
            <NavLink to="/office/boxes" className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="box" />
              Box lookup
            </NavLink>
            <NavLink to="/office/stickers" className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="sticker" />
              Stickers
            </NavLink>
            <NavLink to="/office/transfers" className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="transfer" />
              Transfers
              {onTheRoad.data && onTheRoad.data.length > 0 && <span className="count">{onTheRoad.data.length}</span>}
            </NavLink>
            <NavLink to="/office/counts" className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="count" />
              Counts
              {unresolved.data && unresolved.data.length > 0 && <span className="count">{unresolved.data.length}</span>}
            </NavLink>
            <NavLink to="/office/help" className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="help" />
              Help
            </NavLink>
            {/* Everyone's own: above Admin, which holds the admin's Assistant page. */}
            <NavLink to="/office/whatsapp" className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="link" />
              WhatsApp
            </NavLink>
            <div className="nav-label caps">Admin</div>
            {can(me.data.role, 'settings') && (
              <NavLink to="/office/assistant" className={({ isActive }) => (isActive ? 'on' : undefined)}>
                <Icon name="bolt" />
                Assistant
              </NavLink>
            )}
            <NavLink to="/office/settings" className={({ isActive }) => (isActive ? 'on' : undefined)}>
              <Icon name="settings" />
              Settings
            </NavLink>
          </nav>
          <div className="side-foot">
            <div className="avatar">{initials(me.data.user.name)}</div>
            <div className="grow">
              <b>{me.data.user.name}</b>
              <small>{roleLabel(me.data.role, me.data.siteIds, siteCodes)}</small>
            </div>
            <div className={s.account}>
              <Link to="/office/password" className="ico-btn" aria-label="Change password" title="Change password">
                <Icon name="lock" />
              </Link>
              <button type="button" className="ico-btn" aria-label="Sign out" title="Sign out" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
                <Icon name="undo" />
              </button>
            </div>
          </div>
        </aside>
        {menu.open && <div className="side-scrim" data-testid="menu-backdrop" aria-hidden="true" onClick={menu.close} />}

        <main className="main" inert={menu.open}>
          <header className="top">
            <button
              ref={menu.buttonRef}
              type="button"
              className="ico-btn menu-btn"
              aria-label="Menu"
              aria-expanded={menu.open}
              aria-controls="office-menu"
              onClick={menu.show}
            >
              <Icon name="menu" />
            </button>
            <div className="top-title">
              <div className="crumb">{routeHandle.crumb ?? `${me.data.org.name} · ${dayLabel(new Date(), firstTz)}`}</div>
              <h1>{title}</h1>
            </div>
            <span className="grow" />
            {companyWide ? null : minimalHeader ? (
              <span className="chip">Draft · not saved</span>
            ) : (
              <div className="seg">
                <button type="button" className={siteId === undefined ? 'on' : undefined} aria-pressed={siteId === undefined} onClick={() => setSiteId(undefined)}>
                  All sites
                </button>
                {sites.map((site) => (
                  <button
                    key={site.id}
                    type="button"
                    className={siteId === site.id ? 'on' : undefined}
                    aria-pressed={siteId === site.id}
                    onClick={() => setSiteId(site.id)}
                  >
                    <span className="code">{site.shortCode}</span>
                    {site.name}
                  </button>
                ))}
              </div>
            )}
            <form className="search" onSubmit={submitSearch}>
              <Icon name="search" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Scan or type a box code"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ border: 0, outline: 'none', font: 'inherit', color: 'inherit', background: 'transparent', flex: 1, minWidth: 0 }}
              />
              <span className="kbd">/</span>
            </form>
            <LiveDot />
            {!minimalHeader && !companyWide && can(me.data.role, 'orders') && (
              <Link to="/office/orders/new" className="btn primary">
                <Icon name="plus" />
                New order
              </Link>
            )}
          </header>

          <section className="content field">
            <Outlet />
          </section>
        </main>
      </div>
    </div>
  );
}
